import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { normalizeLocale, setRuntimeTranslations, type SupportedLocale } from '../lib/i18n'

export type Locale = SupportedLocale

type TranslationValues = Record<string, string | number>
type TranslationMap = Record<string, string>
type CachedTranslations = {
  etag: string | null
  translations: TranslationMap
}

interface LangContextValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  loading: boolean
  translations: TranslationMap
  t: (el: string, en: string, values?: TranslationValues) => string
  translate: (key: string, fallback: string, values?: TranslationValues) => string
}

const LangContext = createContext<LangContextValue | null>(null)

const STORAGE_KEY = 'helply_locale'
const TRANSLATIONS_CACHE_PREFIX = 'helply_translations_'

function loadLocale(): Locale {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'en' || stored === 'el') return stored
  return 'el'
}

function cacheKey(locale: Locale) {
  return `${TRANSLATIONS_CACHE_PREFIX}${locale}`
}

function loadCachedTranslations(locale: Locale): CachedTranslations | null {
  const cached = localStorage.getItem(cacheKey(locale))
  if (!cached) return null

  try {
    const parsed = JSON.parse(cached) as CachedTranslations
    if (!parsed || typeof parsed !== 'object') return null
    if (!parsed.translations || typeof parsed.translations !== 'object') return null

    return {
      etag: typeof parsed.etag === 'string' ? parsed.etag : null,
      translations: Object.fromEntries(
        Object.entries(parsed.translations).filter((entry): entry is [string, string] => typeof entry[1] === 'string')
      ),
    }
  } catch {
    return null
  }
}

function persistCachedTranslations(locale: Locale, cached: CachedTranslations) {
  localStorage.setItem(cacheKey(locale), JSON.stringify(cached))
}

function interpolate(value: string, values?: TranslationValues) {
  if (!values) return value

  return value.replace(/%\{(\w+)\}/g, (_, key: string) => String(values[key] ?? `%{${key}}`))
}

export function LangProvider({ children }: { children: React.ReactNode }) {
  const initialLocale = loadLocale()
  const [locale, setLocaleState] = useState<Locale>(initialLocale)
  const [translations, setTranslations] = useState<TranslationMap>(
    () => loadCachedTranslations(initialLocale)?.translations ?? {}
  )
  const [loading, setLoading] = useState(false)

  const setLocale = useCallback((next: Locale) => {
    localStorage.setItem(STORAGE_KEY, next)
    setTranslations(loadCachedTranslations(next)?.translations ?? {})
    setLocaleState(next)
  }, [])

  useEffect(() => {
    setRuntimeTranslations(translations)
  }, [translations])

  useEffect(() => {
    const cached = loadCachedTranslations(locale)
    setTranslations(cached?.translations ?? {})

    const controller = new AbortController()
    const headers = cached?.etag ? { 'If-None-Match': cached.etag } : undefined

    setLoading(true)

    fetch(`/api/translations/${normalizeLocale(locale)}`, {
      headers,
      signal: controller.signal,
    })
      .then(async (response) => {
        if (response.status === 304) return
        if (!response.ok) throw new Error(`Failed to load translations for ${locale}`)

        const payload = await response.json() as { translations?: TranslationMap }
        const nextTranslations = payload.translations ?? {}
        const etag = response.headers.get('ETag')

        setTranslations(nextTranslations)
        persistCachedTranslations(locale, {
          etag,
          translations: nextTranslations,
        })
      })
      .catch((error) => {
        if ((error as Error).name === 'AbortError') return
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })

    return () => controller.abort()
  }, [locale])

  const t = useCallback((el: string, en: string, values?: TranslationValues) => {
    const fallback = locale === 'el' ? el : en
    return interpolate(fallback, values)
  }, [locale])

  const translate = useCallback((key: string, fallback: string, values?: TranslationValues) => {
    return interpolate(translations[key] ?? fallback, values)
  }, [translations])

  return (
    <LangContext.Provider value={{ locale, setLocale, loading, translations, t, translate }}>
      {children}
    </LangContext.Provider>
  )
}

export function useLang() {
  const ctx = useContext(LangContext)
  if (!ctx) throw new Error('useLang must be used within LangProvider')
  return ctx
}

export function useScopedTranslations<T extends Record<string, string>>(namespace: string, fallback: T, targetLocale?: Locale): T {
  const { locale, translate } = useLang()
  const canUseRemoteTranslations = !targetLocale || targetLocale === locale

  return useMemo(() => {
    if (!canUseRemoteTranslations) return fallback

    return Object.fromEntries(
      Object.entries(fallback).map(([key, value]) => [key, translate(`${namespace}.${key}`, value)])
    ) as T
  }, [canUseRemoteTranslations, fallback, namespace, translate])
}
